import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { 
  LogOut, 
  Calendar,
  Clock,
  CheckCircle,
  History,
  Users,
  XCircle,
  Menu,
  Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  available: "Disponibile",
  pending_court: "Attesa Campo",
  confirmed: "Confermata",
  completed: "Fatta",
  cancelled: "Cancellata"
};

const statusColors = {
  available: "bg-emerald-500",
  pending_court: "bg-amber-500",
  confirmed: "bg-primary",
  completed: "bg-gray-500",
  cancelled: "bg-gray-300 text-gray-700"
};

const MobileNav = ({ user, logout, navItems }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success("Logout effettuato");
    window.location.href = "/";
  };

  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-secondary text-white p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-white rounded-sm flex items-center justify-center">
          <span className="text-primary font-heading font-black text-lg">D</span>
        </div>
        <span className="font-heading font-bold text-sm">DRAKE TENNIS</span>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="text-white">
            <Menu className="w-6 h-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="bg-secondary text-white border-none w-64">
          <div className="py-4">
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-white/60">{user.email}</p>
          </div>
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  location.pathname === item.path
                    ? "bg-primary text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
          <div className="absolute bottom-4 left-4 right-4">
            <Button
              variant="outline"
              className="w-full border-white/30 text-white hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Sidebar = ({ user, logout }) => {
  const location = useLocation();

  const handleLogout = () => {
    logout();
    toast.success("Logout effettuato");
    window.location.href = "/";
  };

  const navItems = [
    { path: "/student", icon: Calendar, label: "Prenota" },
    { path: "/student/my-lessons", icon: History, label: "Le Mie Lezioni" },
    { path: "/student/my-packages", icon: Package, label: "I Miei Pacchetti" },
  ];

  return (
    <div className="hidden lg:flex w-64 bg-secondary text-white min-h-screen flex-col">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center">
            <span className="text-primary font-heading font-black text-xl">D</span>
          </div>
          <div>
            <p className="font-heading font-bold tracking-tight">DRAKE TENNIS</p>
            <p className="text-xs text-white/60">Area Allievo</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors ${
              location.pathname === item.path
                ? "bg-primary text-white"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="mb-4 px-4">
          <p className="font-medium truncate">{user.name}</p>
          <p className="text-xs text-white/60 truncate">{user.email}</p>
        </div>
        <Button
          variant="outline"
          className="w-full border-white/30 text-white hover:bg-white/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
};

const BookingPage = () => {
  const { getAuthHeaders } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAvailable = async () => {
    try {
      const response = await axios.get(`${API}/lessons/available`, {
        headers: getAuthHeaders(),
      });
      setLessons(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle disponibilità");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailable();
  }, []);

  const handleBook = async (lessonId) => {
    try {
      await axios.put(
        `${API}/lessons/${lessonId}/book`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Prenotazione effettuata! In attesa conferma campo.");
      fetchAvailable();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella prenotazione");
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="booking-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
          Prenota Lezione
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">Scegli tra le disponibilità del coach</p>
      </div>

      {loading ? (
        <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
      ) : lessons.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground text-lg">
              Nessuna disponibilità al momento
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Torna più tardi per nuove disponibilità
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lessons.map((lesson) => (
            <Card 
              key={lesson.id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`lesson-card-${lesson.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Badge className={`${statusColors[lesson.status]} text-white text-xs`}>
                    {statusLabels[lesson.status]}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {lesson.lesson_type === "group" 
                      ? `Gruppo (${lesson.students.length}/${lesson.max_students})`
                      : "Singola"
                    }
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-lg md:text-xl">
                      {new Date(lesson.date).toLocaleDateString("it-IT", {
                        weekday: "short",
                        day: "numeric",
                        month: "short"
                      })}
                    </p>
                    <p className="text-muted-foreground text-sm flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {lesson.time_slot}
                    </p>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Coach
                  </p>
                  <p className="font-medium text-sm">{lesson.coach_name}</p>
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Descrizione
                  </p>
                  <p className="text-sm">{lesson.description}</p>
                </div>

                {lesson.lesson_type === "group" && lesson.students.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    {lesson.students.length} iscritti
                  </div>
                )}

                <Button
                  className="w-full font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]"
                  onClick={() => handleBook(lesson.id)}
                  disabled={lesson.students.length >= lesson.max_students}
                  data-testid={`book-btn-${lesson.id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {lesson.students.length >= lesson.max_students 
                    ? "Completo" 
                    : "Prenota"
                  }
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const MyLessonsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMyLessons = async () => {
    try {
      const response = await axios.get(`${API}/lessons/my`, {
        headers: getAuthHeaders(),
      });
      setLessons(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle lezioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyLessons();
  }, []);

  const handleCancelBooking = async (lessonId) => {
    if (!window.confirm("Sei sicuro di voler annullare la prenotazione?")) return;
    
    try {
      await axios.put(
        `${API}/lessons/${lessonId}/cancel-booking`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Prenotazione annullata");
      fetchMyLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullamento");
    }
  };

  // Separate lessons by status
  const activeLessons = lessons.filter(l => ["pending_court", "confirmed"].includes(l.status));
  const completedLessons = lessons.filter(l => l.status === "completed");

  return (
    <div className="p-4 md:p-8" data-testid="my-lessons-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
          Le Mie Lezioni
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">Visualizza le tue prenotazioni e lo storico</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
        <Card className="border-l-4 border-amber-500">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">In Attesa</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {lessons.filter(l => l.status === "pending_court").length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-primary">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Confermate</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {lessons.filter(l => l.status === "confirmed").length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-gray-500">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Fatte</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {completedLessons.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
      ) : (
        <div className="space-y-6 md:space-y-8">
          {/* Active Lessons */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl md:text-2xl uppercase">
                Lezioni Attive
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeLessons.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nessuna lezione attiva
                </p>
              ) : (
                <div className="space-y-4">
                  {activeLessons.map((lesson) => (
                    <div key={lesson.id} className="border rounded-sm p-4 space-y-3" data-testid={`active-lesson-${lesson.id}`}>
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <p className="font-bold">{lesson.date}</p>
                          <p className="text-sm text-muted-foreground">{lesson.time_slot}</p>
                        </div>
                        <Badge className={`${statusColors[lesson.status]} text-white text-xs`}>
                          {statusLabels[lesson.status]}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Coach: </span>
                          {lesson.coach_name}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Tipo: </span>
                          {lesson.lesson_type === "group" ? "Gruppo" : "Singola"}
                        </div>
                        {lesson.court_info && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Campo: </span>
                            {lesson.court_info}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCancelBooking(lesson.id)}
                        className="w-full sm:w-auto"
                        data-testid={`cancel-booking-btn-${lesson.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Annulla Prenotazione
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl md:text-2xl uppercase">
                Storico Lezioni
              </CardTitle>
            </CardHeader>
            <CardContent>
              {completedLessons.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nessuna lezione completata
                </p>
              ) : (
                <div className="space-y-3">
                  {completedLessons.map((lesson) => (
                    <div key={lesson.id} className="border rounded-sm p-4 opacity-75" data-testid={`completed-lesson-${lesson.id}`}>
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <p className="font-bold">{lesson.date}</p>
                          <p className="text-sm text-muted-foreground">{lesson.time_slot}</p>
                        </div>
                        <Badge variant="secondary" className="text-xs">Fatta</Badge>
                      </div>
                      <div className="mt-2 text-sm">
                        <span className="text-muted-foreground">Coach: </span>
                        {lesson.coach_name}
                        <span className="mx-2">•</span>
                        <span className="text-muted-foreground">Tipo: </span>
                        {lesson.lesson_type === "group" ? "Gruppo" : "Singola"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const MyPackagesPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages/student/${user.id}`, {
        headers: getAuthHeaders(),
      });
      setPackages(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei pacchetti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const totalRemaining = packages.reduce((sum, p) => sum + p.remaining_lessons, 0);
  const totalLessons = packages.reduce((sum, p) => sum + p.total_lessons, 0);

  return (
    <div className="p-4 md:p-8" data-testid="my-packages-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
          I Miei Pacchetti
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">Visualizza le tue lezioni a scalare</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
        <Card className="border-l-4 border-primary">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Lezioni Rimanenti</p>
            <p className="text-3xl md:text-4xl font-heading font-bold text-primary">
              {totalRemaining}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-gray-400">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Pacchetti Attivi</p>
            <p className="text-3xl md:text-4xl font-heading font-bold">
              {packages.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
      ) : packages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground text-lg">
              Nessun pacchetto attivo
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Contatta il coach per acquistare un pacchetto lezioni
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {packages.map((pkg) => (
            <Card key={pkg.id} data-testid={`package-card-${pkg.id}`}>
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-sm flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-2xl md:text-3xl font-heading font-bold text-primary">
                          {pkg.remaining_lessons}
                        </p>
                        <p className="text-xs text-muted-foreground">/ {pkg.total_lessons}</p>
                      </div>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-lg md:text-xl uppercase">
                        Pacchetto {pkg.total_lessons} Lezioni
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Creato il {new Date(pkg.created_at).toLocaleDateString("it-IT")}
                      </p>
                      {pkg.notes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {pkg.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {pkg.remaining_lessons <= 2 && pkg.remaining_lessons > 0 && (
                      <Badge className="bg-amber-500 text-white">
                        Quasi esaurito!
                      </Badge>
                    )}
                    {pkg.remaining_lessons === 0 && (
                      <Badge variant="secondary">
                        Esaurito
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Lezioni utilizzate</span>
                    <span>{pkg.total_lessons - pkg.remaining_lessons} / {pkg.total_lessons}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all"
                      style={{ width: `${((pkg.total_lessons - pkg.remaining_lessons) / pkg.total_lessons) * 100}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const StudentDashboard = () => {
  const { user, logout } = useAuth();

  const navItems = [
    { path: "/student", icon: Calendar, label: "Prenota" },
    { path: "/student/my-lessons", icon: History, label: "Le Mie Lezioni" },
    { path: "/student/my-packages", icon: Package, label: "I Miei Pacchetti" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <MobileNav user={user} logout={logout} navItems={navItems} />
      <div className="flex">
        <Sidebar user={user} logout={logout} />
        <div className="flex-1 overflow-auto pt-16 lg:pt-0">
          <Routes>
            <Route index element={<BookingPage />} />
            <Route path="my-lessons" element={<MyLessonsPage />} />
            <Route path="my-packages" element={<MyPackagesPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
